import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export type ScheduledPostDeliveryChannel = "telegram" | "whatsapp";
export type ScheduledPostDeliveryStatus = "pending" | "sent" | "failed";
export type ScheduledPostStatus = "pending" | "sending" | "sent" | "failed" | "canceled";

export interface ScheduledPostDelivery {
  id: string;
  channel: ScheduledPostDeliveryChannel;
  targetId: string;
  label: string | null;
  status: ScheduledPostDeliveryStatus;
  lastError: string | null;
  sentAt: string | null;
}

export interface ScheduledPost {
  id: string;
  tenantId: string;
  bodyText: string;
  imageUrl: string | null;
  linkId: string | null;
  scheduledAt: string;
  status: ScheduledPostStatus;
  createdAt: string;
  deliveries: ScheduledPostDelivery[];
}

interface ScheduledPostRow {
  id: string;
  tenant_id: string;
  body_text: string;
  image_url: string | null;
  link_id: string | null;
  scheduled_at: string;
  status: string;
  created_at: string;
}

interface ScheduledPostDeliveryRow {
  id: string;
  scheduled_post_id: string;
  channel: ScheduledPostDeliveryChannel;
  target_id: string;
  label: string | null;
  status: string;
  last_error: string | null;
  sent_at: string | null;
}

const scheduledPostsKey = ["scheduled-posts"] as const;

// ---------------------------------------------------------------------------
// Leitura (post + entregas)
// ---------------------------------------------------------------------------

async function fetchScheduledPosts(): Promise<ScheduledPost[]> {
  const { data: posts, error: postsError } = await supabase
    .from("scheduled_posts")
    .select("id, tenant_id, body_text, image_url, link_id, scheduled_at, status, created_at")
    .order("scheduled_at", { ascending: false })
    .limit(200);

  if (postsError) throw postsError;

  const postRows = (posts ?? []) as ScheduledPostRow[];
  if (postRows.length === 0) {
    return [];
  }

  const { data: deliveries, error: deliveriesError } = await supabase
    .from("scheduled_post_deliveries")
    .select("id, scheduled_post_id, channel, target_id, label, status, last_error, sent_at")
    .in(
      "scheduled_post_id",
      postRows.map((row) => row.id),
    );

  if (deliveriesError) throw deliveriesError;

  const deliveryRows = (deliveries ?? []) as ScheduledPostDeliveryRow[];
  const deliveriesByPost = new Map<string, ScheduledPostDelivery[]>();
  for (const row of deliveryRows) {
    const list = deliveriesByPost.get(row.scheduled_post_id) ?? [];
    list.push({
      id: row.id,
      channel: row.channel,
      targetId: row.target_id,
      label: row.label,
      status: row.status as ScheduledPostDeliveryStatus,
      lastError: row.last_error,
      sentAt: row.sent_at,
    });
    deliveriesByPost.set(row.scheduled_post_id, list);
  }

  return postRows.map((row) => ({
    id: row.id,
    tenantId: row.tenant_id,
    bodyText: row.body_text,
    imageUrl: row.image_url,
    linkId: row.link_id,
    scheduledAt: row.scheduled_at,
    status: row.status as ScheduledPostStatus,
    createdAt: row.created_at,
    deliveries: deliveriesByPost.get(row.id) ?? [],
  }));
}

/** Lista os posts agendados/enviados do tenant (ate 200 mais recentes), com
 *  as entregas por destino. Usado pelo CalendarioPage e pela sidebar de
 *  historico do CriarPostsPage. */
export function useScheduledPosts() {
  return useQuery<ScheduledPost[], Error>({
    queryKey: scheduledPostsKey,
    queryFn: fetchScheduledPosts,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

// ---------------------------------------------------------------------------
// Criacao (envio imediato ou agendado)
// ---------------------------------------------------------------------------

export interface CreateScheduledPostDeliveryInput {
  channel: ScheduledPostDeliveryChannel;
  targetId: string;
  label: string | null;
}

export interface CreateScheduledPostInput {
  bodyText: string;
  imageUrl?: string | null;
  linkId?: string | null;
  /** ISO em UTC. null/undefined = enviar assim que o executor rodar (~30s). */
  scheduledAt?: string | null;
  deliveries: CreateScheduledPostDeliveryInput[];
}

async function createScheduledPost(input: CreateScheduledPostInput): Promise<void> {
  const { error } = await supabase.rpc("create_scheduled_post", {
    p_body_text: input.bodyText,
    p_image_url: input.imageUrl ?? null,
    p_link_id: input.linkId ?? null,
    p_scheduled_at: input.scheduledAt ?? null,
    p_deliveries: input.deliveries.map((delivery) => ({
      channel: delivery.channel,
      targetId: delivery.targetId,
      label: delivery.label,
    })),
  });

  if (error) throw error;
}

export function useCreateScheduledPost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createScheduledPost,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: scheduledPostsKey });
    },
  });
}

// ---------------------------------------------------------------------------
// Cancelamento (pending -> canceled)
// ---------------------------------------------------------------------------

async function cancelScheduledPost(scheduledPostId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("cancel_scheduled_post", {
    p_scheduled_post_id: scheduledPostId,
  });

  if (error) throw error;
  return Boolean(data);
}

export function useCancelScheduledPost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelScheduledPost,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: scheduledPostsKey });
    },
  });
}
